import { PageBuilderContactFormPropertiesModel } from "@de-trustleap/shared-models";
import { TextFieldWidget } from './textFieldWidget';
import { TextAreaWidget } from './textAreaWidget';
import { ButtonWidget } from './buttonWidget';
import { CheckboxWidget } from './checkboxWidget';
import { PagebuilderContactFormSubmission } from '../models/pagebuilderContactFormSubmission';
import { sendContactForm } from '../contactFormFunction';
import { Spinner } from "../helpers/spinner";
import { PageBuilderCheckboxPropertiesModel } from '../models/pageBuilderCheckboxPropertiesModel';
import { getInitialInfo, getTermsAndConditions } from '../utils/getLegalDocument';
import { isPreviewMode,  } from "../utils/previewUtils";
import { buildLegalLink } from "../utils/legalLinkUtils";

export class FormWidget {
    private properties: PageBuilderContactFormPropertiesModel;
    private lastSubmissionTime = 0;
    private disableInlineStyles: boolean;

    constructor(properties: PageBuilderContactFormPropertiesModel, disableInlineStyles = false) {
        this.properties = properties;
        this.disableInlineStyles = disableInlineStyles;

    }

    public async createForm(): Promise<HTMLFormElement> {
        const form = document.createElement('form');
        form.classList.add('form-widget');

        if (!this.disableInlineStyles) {
            form.style.display = 'flex';
            form.style.flexDirection = 'column';
            form.style.justifyContent = 'center';
            form.style.alignItems = 'center';
            form.style.margin = '0 auto';
            form.style.gap = '16px';
        }
        // 🔥 Sichere Hilfsfunktion: Escape für Nutzereingaben (XSS-Schutz)
        const escapeInput = (input: string | null): string => {
            return input ? input.replace(/[&<>"']/g, (char) => {
                const map: Record<string, string> = {
                    '&': '&amp;',
                    '<': '&lt;',
                    '>': '&gt;',
                    '"': '&quot;',
                    "'": '&#39;',
                };
                return map[char] || char;
            }) : '';
        };

        // 🔥 Clickjacking-Schutz aktivieren
        if (window.top && window.top !== window.self) {
            window.top.location.href = window.self.location.href;
        }

        // Hilfsfunktion: Sicherstellen, dass jedes Element eine ID hat
        const ensureId = (element: HTMLElement, prefix: string): string => {
            if (!element.id) {
                element.id = `${prefix}-${Math.random().toString(36).substr(2, 9)}`;
            }
            return element.id;
        };

        // Hilfsfunktion: Platzhalter mit rotem Sternchen für Pflichtfelder
        const getPlaceholderText = (placeholder: string, isRequired: boolean): string => {
            return isRequired ? `${placeholder} *` : placeholder;
        };

        const applyResponsiveWidth = (element: HTMLElement) => {
            const originalWidth = element.style.width;
            const originalMaxWidth = element.style.maxWidth;

            const updateWidth = () => {
                if (window.innerWidth < 768) {
                    element.style.setProperty("width", "100%", "important");
                    element.style.setProperty("max-width", "100%", "important");
                } else {
                    if (originalWidth) {
                        element.style.setProperty("width", originalWidth);
                    } else {
                        element.style.removeProperty("width");
                    }

                    if (originalMaxWidth) {
                        element.style.setProperty("max-width", originalMaxWidth);
                    } else {
                        element.style.removeProperty("max-width");
                    }
                }
            };

            updateWidth();
            window.addEventListener("resize", updateWidth);
        };


        // 🕵️‍♂️ Honeypot-Feld für Spam-Schutz
        const honeypotField = document.createElement('input');
        honeypotField.setAttribute('type', 'text');
        honeypotField.setAttribute('name', 'honeyoo');
        honeypotField.setAttribute('autocomplete', 'off');
        honeypotField.setAttribute('tabindex', '-1');
        honeypotField.setAttribute('aria-hidden', 'true');
        honeypotField.style.position = 'absolute';
        honeypotField.style.left = '-9999px';
        honeypotField.style.height = '1px';
        honeypotField.style.width = '1px';
        honeypotField.style.opacity = '0';

        form.appendChild(honeypotField);

        // 🔹 Name-Feld
        if (this.properties.nameTextFieldProperties) {
            const nameFieldWidget = new TextFieldWidget(this.properties.nameTextFieldProperties, this.disableInlineStyles);
            const nameFieldWrapper = await nameFieldWidget.getWidget();
            const nameInput = nameFieldWrapper.querySelector('input');

            if (nameInput) {
                nameInput.setAttribute('name', 'name');
                ensureId(nameInput, "name");
                nameInput.placeholder = getPlaceholderText("Name", this.properties.nameTextFieldProperties.isRequired ?? false);
                if (this.properties.nameTextFieldProperties.isRequired) {
                    nameInput.setAttribute('required', 'true');
                }
                applyResponsiveWidth(nameInput);

            }

            form.appendChild(nameFieldWrapper);
        }

        // 🔹 E-Mail-Feld mit Live-Validierung
        if (this.properties.emailTextFieldProperties) {
            const emailFieldWidget = new TextFieldWidget(this.properties.emailTextFieldProperties, this.disableInlineStyles);
            const emailWrapper = await emailFieldWidget.getWidget();
            const emailInput = emailWrapper.querySelector('input');

            if (emailInput) {
                emailInput.setAttribute('type', 'email');
                emailInput.setAttribute('name', 'senderEmail');
                ensureId(emailInput, "email");

                // 🔹 Setze Platzhalter mit Sternchen für Pflichtfelder
                emailInput.placeholder = getPlaceholderText(
                    "E-Mail",
                    this.properties.emailTextFieldProperties.isRequired ?? false
                );

                if (this.properties.emailTextFieldProperties.isRequired) {
                    emailInput.setAttribute('required', 'true');
                }
                applyResponsiveWidth(emailInput);

                // 🔹 Standardfarben speichern (Hintergrund & Border)
                const defaultBackgroundColor = emailInput.style.backgroundColor || "white";
                const defaultBorderColor = emailInput.style.borderColor || "#ccc";

                // 🔹 Regex + Validierung
                const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
                const emailHint = document.createElement('small');
                emailHint.innerText = "Ungültige E-Mail-Adresse! Beispiel: name@domain.com";
                emailHint.style.color = "red";
                emailHint.style.fontSize = "16px";
                emailHint.style.marginTop = "4px";
                emailHint.style.display = "none";

                emailInput.addEventListener("input", (event) => {
                    const input = event.target as HTMLInputElement;
                    const emailValue = input.value.trim();

                    if (emailValue === "") {
                        emailHint.style.display = "none";
                        input.style.backgroundColor = defaultBackgroundColor;
                        input.style.borderColor = defaultBorderColor;
                        input.setCustomValidity("");
                    } else if (!emailRegex.test(emailValue)) {
                        input.setCustomValidity("Ungültige E-Mail-Adresse!");
                        emailHint.style.display = "block";
                        input.style.backgroundColor = "#FAADA8";
                        input.style.borderColor = "red";
                    } else {
                        input.setCustomValidity("");
                        emailHint.style.display = "none";
                        input.style.backgroundColor = defaultBackgroundColor;
                        input.style.borderColor = defaultBorderColor;
                    }
                });

                form.appendChild(emailWrapper);
                form.appendChild(emailHint);
            }
        }

        // 🔹 Telefonnummer-Feld mit Validierung
        if (this.properties.phoneTextFieldProperties) {
            const phoneFieldWidget = new TextFieldWidget(this.properties.phoneTextFieldProperties, this.disableInlineStyles);
            const phoneWrapper = await phoneFieldWidget.getWidget();
            const phoneInput = phoneWrapper.querySelector('input');

            if (phoneInput) {
                phoneInput.setAttribute('type', 'tel');
                phoneInput.setAttribute('name', 'phone');
                ensureId(phoneInput, "phone");
                phoneInput.placeholder = getPlaceholderText("Telefonnummer", this.properties.phoneTextFieldProperties.isRequired ?? false);
                if (this.properties.phoneTextFieldProperties.isRequired) {
                    phoneInput.setAttribute('required', 'true');
                }
                applyResponsiveWidth(phoneInput);

                const defaultBackgroundColor = phoneInput.style.backgroundColor || "white";
                const defaultBorderColor = phoneInput.style.borderColor || "#ccc";

                const phoneRegex = /^(\+?[1-9][0-9]{0,2})?[-.\s]?\(?[0-9]{2,4}\)?[-.\s]?[0-9]{3,5}[-.\s]?[0-9]{3,5}$/;

                const phoneHint = document.createElement('small');
                phoneHint.innerText = "Ungültige Telefonnummer! Beispiel: +49 176 12345678";
                phoneHint.style.color = "red";
                phoneHint.style.fontSize = "16px";
                phoneHint.style.marginTop = "4px";
                phoneHint.style.display = "none";

                phoneInput.addEventListener("input", (event) => {
                    const input = event.target as HTMLInputElement;
                    const phoneValue = input.value.trim();

                    if (phoneValue === "") {
                        phoneHint.style.display = "none";
                        input.style.backgroundColor = defaultBackgroundColor;
                        input.style.borderColor = defaultBorderColor;
                        input.setCustomValidity("");
                    } else if (!phoneRegex.test(phoneValue)) {
                        input.setCustomValidity("Ungültige Telefonnummer!");
                        phoneHint.style.display = "block";
                        input.style.backgroundColor = "#FAADA8";
                        input.style.borderColor = "red";
                    } else {
                        input.setCustomValidity("");
                        phoneHint.style.display = "none";
                        input.style.backgroundColor = defaultBackgroundColor;
                        input.style.borderColor = defaultBorderColor;
                    }
                });

                form.appendChild(phoneWrapper);
                form.appendChild(phoneHint);
            }
        }


        // 🔹 Nachricht-Feld
        if (this.properties.messageTextFieldProperties) {
            const messageFieldWidget = new TextAreaWidget(this.properties.messageTextFieldProperties, this.disableInlineStyles);
            const messageWrapper = await messageFieldWidget.getWidget(); 
            const textarea = messageWrapper.querySelector('textarea');

            if (textarea) {
                textarea.setAttribute('name', 'message');
                ensureId(textarea, "message");
                textarea.placeholder = getPlaceholderText("Nachricht", this.properties.messageTextFieldProperties.isRequired ?? false);
                if (this.properties.messageTextFieldProperties.isRequired) {
                    textarea.setAttribute('required', 'true');
                }
                applyResponsiveWidth(textarea);

                form.appendChild(messageWrapper);
            }
        }

        // **Workaround für fehlende checkboxProperties**
        // **Typenumgehung: TypeScript zwingen, `checkboxProperties` zu akzeptieren**
        const contactFormProperties = this.properties as unknown as {
            checkboxProperties?: PageBuilderCheckboxPropertiesModel,
            initialInfo?: PageBuilderCheckboxPropertiesModel
        };

        // **Manuelle Standardwerte für die Datenschutz-Checkbox**
        const checkboxProperties = new PageBuilderCheckboxPropertiesModel({
            labelText: contactFormProperties.checkboxProperties?.labelText ?? "Ich akzeptiere die Datenschutzbestimmungen.",
            isRequired: contactFormProperties.checkboxProperties?.isRequired ?? true,
            linkText: contactFormProperties.checkboxProperties?.linkText ?? "Datenschutzrichtlinie",
            linkUrl: buildLegalLink("/legalpages/datenschutz.html"),
        });

        // **Manuelle Standardwerte für die Erstinformations-Checkbox**
        const initialInfoProperties = new PageBuilderCheckboxPropertiesModel({
            labelText: contactFormProperties.initialInfo?.labelText ?? "Ich bestätige, die Erstinformation gelesen zu haben.",
            isRequired: contactFormProperties.initialInfo?.isRequired ?? true,
            linkText: contactFormProperties.initialInfo?.linkText ?? "Mehr erfahren",
            linkUrl: buildLegalLink("/legalpages/erstinformation.html"),
        });

        // **Übergeordneter Container für die Checkboxen**
        const checkboxesContainer = document.createElement("div");
        checkboxesContainer.style.display = "flex";
        checkboxesContainer.style.flexDirection = "column";
        checkboxesContainer.style.alignItems = "flex-start";
        checkboxesContainer.style.gap = "12px";
        checkboxesContainer.style.padding = "16px";

        // **🔹 Datenschutzerklärung-Checkbox hinzufügen**
        const privacyCheckboxWidget = new CheckboxWidget(checkboxProperties);
        const privacyCheckboxField = await privacyCheckboxWidget.getWidget();
        const privacyInputElement = privacyCheckboxField.querySelector('input');

        if (privacyInputElement) {
            ensureId(privacyInputElement, "checkbox-privacy");
            privacyInputElement.setAttribute('name', 'privacyConsent');

            // 🔹 Datenschutz-Label erstellen und den Link darin einbetten
            const privacyCheckboxLabel = document.createElement("label");
            privacyCheckboxLabel.setAttribute("for", privacyInputElement.id);
            privacyCheckboxLabel.innerHTML = `
        ${checkboxProperties.labelText} 
        ${checkboxProperties.linkText && checkboxProperties.linkUrl ? `
            <a href="${checkboxProperties.linkUrl}" target="_blank">${checkboxProperties.linkText}</a>` : ''}
    `;

            // **Checkbox mit Label gruppieren**
            const privacyCheckboxContainer = document.createElement("div");
            privacyCheckboxContainer.style.display = "flex";
            privacyCheckboxContainer.style.justifyContent = "center";
            privacyCheckboxContainer.style.alignItems = "center";
            privacyCheckboxContainer.style.gap = "8px";

            privacyCheckboxContainer.appendChild(privacyInputElement);
            privacyCheckboxContainer.appendChild(privacyCheckboxLabel);
            checkboxesContainer.appendChild(privacyCheckboxContainer);
        }

        // **🔹 Erstinformations-Checkbox nur anzeigen, wenn auch im Footer vorhanden**
        const initialInfoText = await getInitialInfo();
        if (initialInfoText?.trim()) {
            const initialInfoCheckboxWidget = new CheckboxWidget(initialInfoProperties);
            const initialInfoCheckboxField = await initialInfoCheckboxWidget.getWidget();
            const initialInfoInputElement = initialInfoCheckboxField.querySelector('input');

            if (initialInfoInputElement) {
                ensureId(initialInfoInputElement, "checkbox-initial-info");
                initialInfoInputElement.setAttribute('name', 'initialInfoConsent');

                // **Erstinformations-Label erstellen und den Link darin einbetten**
                const initialInfoCheckboxLabel = document.createElement("label");
                initialInfoCheckboxLabel.setAttribute("for", initialInfoInputElement.id);
                initialInfoCheckboxLabel.innerHTML = `
            ${initialInfoProperties.labelText} 
            ${initialInfoProperties.linkText && initialInfoProperties.linkUrl ? `
                <a href="${initialInfoProperties.linkUrl}" target="_blank">${initialInfoProperties.linkText}</a>` : ''}
        `;

                // **Checkbox mit Label gruppieren**
                const initialInfoCheckboxContainer = document.createElement("div");
                initialInfoCheckboxContainer.style.display = "flex";
                initialInfoCheckboxContainer.style.alignItems = "center";
                initialInfoCheckboxContainer.style.gap = "8px";

                initialInfoCheckboxContainer.appendChild(initialInfoInputElement);
                initialInfoCheckboxContainer.appendChild(initialInfoCheckboxLabel);
                checkboxesContainer.appendChild(initialInfoCheckboxContainer);
            }
        }

        // 🔹 AGB-Checkbox nur anzeigen, wenn auch im Footer vorhanden
        const termsAndConditionsText = await getTermsAndConditions();
        if (termsAndConditionsText?.trim()) {
            const agbProperties = new PageBuilderCheckboxPropertiesModel({
                labelText: "Ich akzeptiere die Allgemeinen Geschäftsbedingungen (AGB).",
                isRequired: true,
                linkText: "AGB lesen",
                linkUrl: buildLegalLink("/legalpages/agb.html"),
            });

            const agbCheckboxWidget = new CheckboxWidget(agbProperties);
            const agbCheckboxField = await agbCheckboxWidget.getWidget();
            const agbInputElement = agbCheckboxField.querySelector('input');

            if (agbInputElement) {
                ensureId(agbInputElement, "checkbox-agb");
                agbInputElement.setAttribute('name', 'termsConsent');

                const agbCheckboxLabel = document.createElement("label");
                agbCheckboxLabel.setAttribute("for", agbInputElement.id);
                agbCheckboxLabel.innerHTML = `
            ${agbProperties.labelText} 
            ${agbProperties.linkText && agbProperties.linkUrl ? `
                <a href="${agbProperties.linkUrl}" target="_blank">${agbProperties.linkText}</a>` : ''}
        `;

                const agbCheckboxContainer = document.createElement("div");
                agbCheckboxContainer.style.display = "flex";
                agbCheckboxContainer.style.alignItems = "center";
                agbCheckboxContainer.style.gap = "8px";

                agbCheckboxContainer.appendChild(agbInputElement);
                agbCheckboxContainer.appendChild(agbCheckboxLabel);
                checkboxesContainer.appendChild(agbCheckboxContainer);
            }
        }

        // **Checkboxen in das Formular einfügen**
        form.appendChild(checkboxesContainer);

        // 🔹 Senden-Button mit Anti-Spam-Schutz
        if (this.properties.buttonProperties) {
            const buttonWidget = new ButtonWidget(this.properties.buttonProperties, this.disableInlineStyles);
            const submitButton = await buttonWidget.getWidget() as HTMLButtonElement;
            const spinner = Spinner.getInstance("loading-spinner", true);

            if (isPreviewMode()) {
                submitButton.type = "button";
                submitButton.disabled = true;
                submitButton.style.opacity = "0.5";
                submitButton.style.cursor = "not-allowed";
                submitButton.title = "Formular deaktiviert im Vorschau-Modus";

                submitButton.addEventListener('click', (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                });

            } else {
                submitButton.type = "submit";

                form.addEventListener('submit', async (event) => {
                    event.preventDefault();

                    if (honeypotField.value !== "") {
                        console.warn("❌ Spam-Verdacht! Formular nicht gesendet.");
                        return;
                    }

                    const now = Date.now();
                    if (now - this.lastSubmissionTime < 5000) {
                        console.warn("⚠️ Bitte warte 5 Sekunden vor erneutem Absenden.");
                        return;
                    }
                    this.lastSubmissionTime = now;

                    if (form.checkValidity()) {
                        submitButton.disabled = true;
                        spinner.show();

                        const formData = new FormData(form);
                        console.log("📤 Sende Daten...");

                        const getValue = (key: string): string => {
                            const val = formData.get(key);
                            return typeof val === 'string' ? escapeInput(val) : '';
                        };

                        const submission = new PagebuilderContactFormSubmission({
                            name: getValue('name'),
                            senderEmail: getValue('senderEmail').toLowerCase(),
                            recipientEmail: "dennis_schwantes@yahoo.de",
                            phone: getValue('phone'),
                            message: getValue('message'),
                        });

                        try {
                            await sendContactForm(submission);
                            form.reset();
                            this.safeRedirect("/redirect.html");
                        } catch (error) {
                            console.error("❌ Fehler beim Senden:", error);
                        } finally {
                            spinner.hide();
                            setTimeout(() => {
                                submitButton.disabled = false;
                            }, 3000);
                        }
                    } else {
                        form.reportValidity();
                    }
                });
            }

            form.appendChild(submitButton);
        }


        form.appendChild(createTrustBlock());

        return form;
    }

    // 🔥 Sichere Weiterleitung (keine Open Redirects)
    private safeRedirect(url: string) {
        const allowedRedirects = ["/redirect.html", "/thank-you.html"];
        if (allowedRedirects.includes(url)) {
            const currentParams = new URLSearchParams(window.location.search);
            const id = currentParams.get("id");
            const targetUrl = id ? `${url}?id=${encodeURIComponent(id)}` : url;
            window.location.href = targetUrl;
        } else {
            console.warn("🚨 Unsichere Weiterleitung blockiert:", url);
        }
    }
}

function createTrustBlock(): HTMLElement {
    const trustContainer = document.createElement("div");
    trustContainer.style.display = "flex";
    trustContainer.style.flexWrap = "wrap";
    trustContainer.style.justifyContent = "center";
    trustContainer.style.alignItems = "flex-start";
    trustContainer.style.gap = "24px";
    trustContainer.style.padding = "24px 12px";
    trustContainer.style.borderTop = "1px solid #e5e7eb";
    trustContainer.style.backgroundColor = "#fafafa";

    const items = [
        {
            label: "DSGVO-konform",
            color: "#10B981",
            icon: `<path d="M12 1a11 11 0 1 0 11 11A11.012 11.012 0 0 0 12 1Zm0 20a9 9 0 1 1 9-9a9.01 9.01 0 0 1-9 9Z"/><path d="M16 8H8a1 1 0 0 0 0 2h8a1 1 0 0 0 0-2ZM16 12H8a1 1 0 0 0 0 2h8a1 1 0 0 0 0-2Z"/>`
        },
        {
            label: "Keine Weitergabe an Dritte",
            color: "#3B82F6",
            icon: `<path d="M12 2a10 10 0 1 0 10 10A10.011 10.011 0 0 0 12 2Zm1 14h-2v-2h2Zm0-4h-2V7h2Z"/>`
        },
        {
            label: "SSL-verschlüsselte Übertragung",
            color: "#6366F1",
            icon: `<path d="M17 8V7a5 5 0 0 0-10 0v1H5v14h14V8Zm-8-1a3 3 0 0 1 6 0v1H9Zm8 13H7V10h10ZM12 13a2 2 0 1 0 2 2a2 2 0 0 0-2-2Z"/>`
        }
    ];

    for (const item of items) {
        const block = document.createElement("div");
        block.style.display = "flex";
        block.style.flexDirection = "column";
        block.style.alignItems = "center";
        block.style.maxWidth = "140px";
        block.style.textAlign = "center";
        block.style.gap = "6px";

        const iconWrapper = document.createElement("div");
        iconWrapper.style.height = "42px";
        iconWrapper.style.display = "flex";
        iconWrapper.style.alignItems = "center";
        iconWrapper.style.justifyContent = "center";

        const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
        svg.setAttribute("width", "32");
        svg.setAttribute("height", "32");
        svg.setAttribute("viewBox", "0 0 24 24");
        svg.setAttribute("fill", item.color);
        svg.innerHTML = item.icon;
        svg.style.opacity = "0.85";

        iconWrapper.appendChild(svg);

        const label = document.createElement("div");
        label.textContent = item.label;
        label.style.fontSize = "13px";
        label.style.fontWeight = "500";
        label.style.fontFamily = "'Inter', sans-serif";
        label.style.color = "#444";

        block.appendChild(iconWrapper);
        block.appendChild(label);
        trustContainer.appendChild(block);
    }

    return trustContainer;
}
